import { FC, useState, MouseEvent } from 'react';
import { Box } from '@mui/material';
import MainLayout from 'src/pages/private';
import SearchBar from 'src/components/InputFields/Searchbar';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';
import {
  PrimaryActionButton,
  FilterButton,
  FilterIcon,
  FilterPopoverIcon,
  FilterPopoverTitle,
  FilterPopoverDivider,
  FilterCloseIcon,
  FilterLabel,
  SmallActionButton,
  DateLabel,
  ErrorHelperText,
  ErrorHelperTextContainer,
  FlexCenterContainer,
  FlexCenterHorizontalContainer,
  FlexSpaceBetweenContainer,
  FlexSpaceBetweenDateContainer,
  FlexContainer,
  FlexColumnContainer,
  FlexGapContainer,
  FlexItem,
} from 'src/components/StyledComponents/StyledPageComponents';
import ProductionGrid from 'src/components/Grids/ProductionGrid';
import { useFormik } from 'formik';
import {
  productionFilterValidation,
  productionValidation,
} from 'src/utils/validationScheme.utils';
import { addProduction } from 'src/services/production.service';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  productionDialogTitles,
  productionDialogSubtitles,
  DATE_FORMAT,
} from 'src/utils/properties.utils';
import { productionFilterInitialValues } from 'src/utils/initialValues.utils';
import ProductionDialog from 'src/components/DialogBox/ProductionBox/ProductionDialog';
import TextField from 'src/components/InputFields/TextField';
import ProductSelect from 'src/components/InputFields/ProductPicker';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import FilterPopover from 'src/components/StyledComponents/FilterPopover';
import dayjs from 'dayjs';

interface ProductionFormValues {
  productId: string;
  producedQuantity: string;
  damagedQuantity: string;
  productionDate: string | null;
}

const ProductionManagement: FC = () => {
  const { showSnackbar } = StyledSnackbar();

  // Helper to convert local date to UTC midnight to prevent timezone shift
  const toLocalDateAsUTC = (date: Date | null): Date | null => {
    if (!date) return null;
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
  };

  const formik = useFormik({
    initialValues: {
      productId: '',
      producedQuantity: '',
      damagedQuantity: '',
      productionDate: new Date().toISOString().split('T')[0],
    },
    validationSchema: productionValidation,
    onSubmit: (values) => handleSubmit(values),
  });

  const filterFormik = useFormik({
    initialValues: productionFilterInitialValues,
    validationSchema: productionFilterValidation,
    onSubmit: (values) => {
      setFilterValues({
        productId: values.productPicker,
        startDate: toLocalDateAsUTC(values.startDate),
        endDate: toLocalDateAsUTC(values.endDate),
      });
      handleFilterClose();
    },
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [productionAdded, setProductionAdded] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterValues, setFilterValues] = useState({
    productId: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
  });

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return dayjs(date).format(DATE_FORMAT);
  };

  const handleSubmit = async (values: ProductionFormValues) => {
    setLoading(true);

    try {
      const response = await addProduction({
        productId: values.productId,
        producedQuantity: Number(values.producedQuantity),
        damagedQuantity: Number(values.damagedQuantity),
        productionDate: formatDate(values.productionDate as string),
      });
      showSnackbar(response.message, 'success');
      setProductionAdded(!productionAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const handleFilterClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setFilterOpen(true);
  };

  const handleClearAndClose = () => {
    filterFormik.resetForm();
    setFilterValues({
      productId: '',
      startDate: null,
      endDate: null,
    });
    setFilterOpen(false);
  };

  const handleFilterClose = () => {
    setFilterOpen(false);
  };

  return (
    <MainLayout
      title="Production Management"
      searchBar={
        <SearchBar
          width="300px"
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search Productions by product name"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="130px"
          onClick={handleClickOpen}
        >
          <i className="bx bx-plus" /> Production
        </PrimaryActionButton>
      }
      filterButton={
        <FilterButton
          className="btn-filter"
          width="100px"
          onClick={handleFilterClick}
        >
          <FilterIcon component="i" className="bx bx-filter" />
          Add Filter
        </FilterButton>
      }
    >
      <FilterPopover
        open={filterOpen}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
      >
        <Box>
          <FlexCenterContainer>
            <FilterPopoverIcon component="i" className="bx bx-filter" />
            <FilterPopoverTitle>Filter Data By</FilterPopoverTitle>
          </FlexCenterContainer>
          <FilterPopoverDivider />
          <FilterCloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={handleFilterClose}
          />
        </Box>
        <FlexCenterHorizontalContainer>
          <ProductSelect
            id="productPicker"
            label="Product"
            height="28px"
            width="320px"
            placeholder="Product"
            color="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={filterFormik}
          />
        </FlexCenterHorizontalContainer>
        <Box>
          <FilterLabel>Production Date:</FilterLabel>
          <FlexSpaceBetweenDateContainer>
            <CreatedDatePicker
              name="startDate"
              placeholder="Start Date"
              formikProps={filterFormik}
            />
            <CreatedDatePicker
              name="endDate"
              placeholder="End Date"
              formikProps={filterFormik}
            />
          </FlexSpaceBetweenDateContainer>
          {filterFormik.touched.endDate && filterFormik.errors.endDate && (
            <ErrorHelperTextContainer>
              {filterFormik.errors.endDate}
            </ErrorHelperTextContainer>
          )}
        </Box>
        <FlexSpaceBetweenContainer>
          <SmallActionButton
            btnVariant="inactive"
            className="btn-inactive"
            width="70px"
            mr={2}
            onClick={() => {
              filterFormik.resetForm();
              setFilterValues({
                productId: '',
                startDate: null,
                endDate: null,
              });
            }}
          >
            Clear
          </SmallActionButton>
          <SmallActionButton
            className="btn-primary"
            width="70px"
            onClick={() => filterFormik.handleSubmit()}
          >
            Apply
          </SmallActionButton>
        </FlexSpaceBetweenContainer>
      </FilterPopover>
      <ProductionGrid
        state={productionAdded}
        searchTerm={searchTerm}
        filters={filterValues}
      />
      <FlexContainer>
        <ProductionDialog
          width="840px"
          height="500px"
          open={open}
          handleClose={handleClose}
          title={productionDialogTitles.ADD}
          subtitle={productionDialogSubtitles.ADD}
          onSubmit={formik.handleSubmit}
          buttonDisabled={loading}
          buttonLoading={loading}
          buttonLabel="Save"
        >
          <FlexColumnContainer>
            <FlexGapContainer mt={1.5}>
              <FlexItem>
                <ProductSelect
                  color="var(--subtext)"
                  width="100%"
                  id="productId"
                  label="Product"
                  formikProps={formik}
                  showLabel={true}
                  errorState={
                    formik.submitCount > 0 && Boolean(formik.errors.productId)
                  }
                />
              </FlexItem>
              <FlexItem mt={0.5}>
                <DateLabel>Production Date</DateLabel>
                <CreatedDatePicker
                  name="productionDate"
                  width="100%"
                  height="40px"
                  fontSize="16px"
                  placeholder="Select production date"
                  formikProps={formik}
                  errorState={
                    formik.submitCount > 0 &&
                    Boolean(formik.errors.productionDate)
                  }
                />
                {(formik.touched.productionDate || formik.submitCount > 0) &&
                  formik.errors.productionDate && (
                    <ErrorHelperText marginTop="-8px">
                      <ErrorIcon />
                      {String(formik.errors.productionDate)}
                    </ErrorHelperText>
                  )}
              </FlexItem>
            </FlexGapContainer>
            <FlexGapContainer mt={2} mb={5}>
              <FlexItem>
                <TextField
                  showLabel={true}
                  color="var(--subtext)"
                  width="100%"
                  id="producedQuantity"
                  label="Produced Quantity"
                  formikProps={formik}
                />
              </FlexItem>
              <FlexItem>
                <TextField
                  showLabel={true}
                  color="var(--subtext)"
                  width="100%"
                  id="damagedQuantity"
                  label="Damaged Quantity"
                  formikProps={formik}
                />
              </FlexItem>
            </FlexGapContainer>
          </FlexColumnContainer>
        </ProductionDialog>
      </FlexContainer>
    </MainLayout>
  );
};

export default ProductionManagement;
